import type { ColumnResizedEvent, GridApi, SortChangedEvent } from "ag-grid-community";
import { useCallback } from "react";
import { useAppDispatch } from "../../../../../hooks";
import { debounce } from "../../../../utils";
import { setIssueSortedColInRedux, updateColumnStateThunk } from "../../issueSlice";
import { useDragColumnChange } from "../../issueUtils";

type ColumnStateUpdateEvent = {
  api: GridApi;
};

export const useIssueTableColumnStateHandlers = () => {
  const dispatch = useAppDispatch();

  const getColumnState = useCallback((event: ColumnStateUpdateEvent) => {
    return (
      event.api.getAllDisplayedColumns()?.map((col, index) => ({
        colId: col.getColId(),
        orderPriority: index,
        width: col.getActualWidth(),
      })) ?? []
    );
  }, []);

  const handleColumnStateUpdate = useCallback(
    (event: ColumnStateUpdateEvent) => {
      dispatch(updateColumnStateThunk(getColumnState(event)));
    },
    [dispatch, getColumnState],
  );

  const handleColumnLayoutUpdate = useCallback(
    (event: ColumnStateUpdateEvent) => {
      dispatch(updateColumnStateThunk(getColumnState(event)));
    },
    [dispatch, getColumnState],
  );

  const debouncedHandleToggleColumnVisibility = useCallback(
    debounce((event: ColumnStateUpdateEvent) => {
      handleColumnStateUpdate(event);
    }, 1000),
    [handleColumnStateUpdate],
  );

  const onSortChanged = useCallback(
    (params: SortChangedEvent) => {
      const updatedColumn = params.api.getColumnState().find((state) => state.sort != null);
      const formattedColumn =
        updatedColumn?.sort != null
          ? { column: updatedColumn.colId, direction: updatedColumn.sort.toUpperCase() }
          : null;

      dispatch(setIssueSortedColInRedux(formattedColumn));
    },
    [dispatch],
  );

  const { onDragStarted, onDragStopped } = useDragColumnChange(handleColumnLayoutUpdate);

  const onColumnResized = useCallback(
    (event: ColumnResizedEvent) => {
      if (event.finished !== true || event.source !== "uiColumnResized") {
        return;
      }

      handleColumnLayoutUpdate(event);
    },
    [handleColumnLayoutUpdate],
  );

  return {
    debouncedHandleToggleColumnVisibility,
    onColumnResized,
    onDragStarted,
    onDragStopped,
    onSortChanged,
  };
};
